import { useEffect } from 'react';
import { toast } from 'sonner';
import { fetchTasks, TASK_REMINDERS_CHANGED_EVENT } from '../services/taskApi';
import { ReminderSoundLoopController } from '../services/taskReminderAudio';
import {
  normalizeReminderConfig,
  TaskReminderScheduler,
} from '../services/taskReminderScheduler';

const POLL_INTERVAL_MS = 60_000;
const MAX_PAGES = 20;

function timingDescription(offset) {
  if (offset === 0) return 'This task is due now.';
  if (offset < 0) return 'Snoozed reminder.';
  if (offset < 60) return `Due in ${offset} minute${offset === 1 ? '' : 's'}.`;
  if (offset < 1440 && offset % 60 === 0) {
    const hours = offset / 60;
    return `Due in ${hours} hour${hours === 1 ? '' : 's'}.`;
  }
  if (offset % 1440 === 0) {
    const days = offset / 1440;
    return `Due in ${days} day${days === 1 ? '' : 's'}.`;
  }
  return `Due in ${offset} minutes.`;
}

async function loadVisibleTasks(workspace) {
  const tasks = [];
  let page = 1;
  let totalPages = 1;
  do {
    const response = await fetchTasks(workspace, {
      page,
      pageSize: 100,
      sortField: 'due_at',
      sortDirection: 'asc',
    });
    tasks.push(...(response.items || []));
    totalPages = Math.min(Number(response.total_pages || 1), MAX_PAGES);
    page += 1;
  } while (page <= totalPages);
  return tasks;
}

function showBrowserNotification(task, reminder, offset) {
  if (
    !reminder.browserNotification
    || typeof window === 'undefined'
    || !('Notification' in window)
    || window.Notification.permission !== 'granted'
  ) return;
  try {
    const notification = new window.Notification(
      offset === 0 ? 'Task Due Now' : 'Task Reminder',
      {
        body: `${task.title}\n${timingDescription(offset)}`,
        tag: `task-reminder:${task.taskId}:${offset}`,
      },
    );
    window.setTimeout(() => notification.close(), 15_000);
  } catch {
    // Browser notification failures must not block in-app delivery.
  }
}

function inAppReminderDuration(reminder) {
  if (!reminder.sound || reminder.soundType === 'none') return 30_000;
  if (reminder.soundRepeatMode === 'until_dismissed') return Infinity;
  if (reminder.soundRepeatMode === 'duration') {
    return (Number(reminder.soundDurationSeconds) * 1000) + 5_000;
  }
  return 30_000;
}

export function TaskReminderProvider({ workspace }) {
  const scopeKey = `${workspace?.scopeType || ''}:${workspace?.agencyId || workspace?.platformId || ''}`;

  useEffect(() => {
    if (!workspace?.scopeType) return undefined;
    let disposed = false;
    let refreshing = false;
    let refreshTimer = null;
    let scheduler;
    let activeToastId = null;
    const soundController = new ReminderSoundLoopController();

    const deliver = ({ task, reminder, offset }) => {
      soundController.stop();
      if (activeToastId != null) toast.dismiss(activeToastId);
      const toastId = toast(
        offset === 0 ? 'Task Due Now' : 'Task Reminder',
        {
          description: `${task.title} — ${timingDescription(offset)}`,
          duration: inAppReminderDuration(reminder),
          action: {
            label: 'Snooze 5 minutes',
            onClick: () => {
              soundController.stop();
              toast.dismiss(toastId);
              activeToastId = null;
              scheduler.snooze(task, reminder, 5);
            },
          },
          cancel: {
            label: 'Dismiss',
            onClick: () => {
              soundController.stop();
              toast.dismiss(toastId);
              activeToastId = null;
            },
          },
        },
      );
      activeToastId = toastId;
      showBrowserNotification(task, reminder, offset);
      if (reminder.sound && reminder.soundType !== 'none') {
        soundController.start(reminder, { taskId: task.taskId });
      }
    };

    scheduler = new TaskReminderScheduler({ onFire: deliver });

    const refresh = async () => {
      if (disposed || refreshing) return;
      refreshing = true;
      try {
        const tasks = await loadVisibleTasks(workspace);
        if (!disposed) {
          scheduler.reconcile(tasks);
          if (soundController.activeTaskId) {
            const activeTask = tasks.find(
              (task) => task.taskId === soundController.activeTaskId,
            );
            const activeReminder = normalizeReminderConfig(activeTask?.reminders);
            if (
              !activeTask
              || activeTask.status === 'completed'
              || !activeReminder.enabled
              || !activeReminder.sound
              || activeReminder.soundType === 'none'
            ) {
              soundController.stop();
              if (activeToastId != null) toast.dismiss(activeToastId);
              activeToastId = null;
            }
          }
        }
      } catch {
        // Entitlement, transient network, and logged-out states are retried by polling.
      } finally {
        refreshing = false;
      }
    };

    const scheduleRefresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(refresh, 150);
    };
    const handleTaskChanged = (event) => {
      if (
        soundController.activeTaskId
        && event?.detail?.taskId === soundController.activeTaskId
        && ['update', 'complete', 'delete'].includes(event?.detail?.operation)
      ) {
        soundController.stop();
        if (activeToastId != null) toast.dismiss(activeToastId);
        activeToastId = null;
      }
      scheduleRefresh();
    };

    refresh();
    const poller = window.setInterval(refresh, POLL_INTERVAL_MS);
    window.addEventListener(TASK_REMINDERS_CHANGED_EVENT, handleTaskChanged);

    return () => {
      disposed = true;
      window.clearInterval(poller);
      window.clearTimeout(refreshTimer);
      window.removeEventListener(TASK_REMINDERS_CHANGED_EVENT, handleTaskChanged);
      scheduler.destroy();
      soundController.destroy();
      if (activeToastId != null) toast.dismiss(activeToastId);
    };
  }, [scopeKey, workspace]);

  return null;
}

export default TaskReminderProvider;
